/**
 * Admin password reset v2 + self-change password v2 — client orchestration.
 *
 * The admin's client owns the cryptographic work: it has the investigation's
 * raw KEKs in localStorage (unwrapped at loginV2), generates the temp password,
 * derives a provisional wrapping key, re-wraps the KEKs, and posts the bundle
 * to the API. The server only sees pre-wrapped material — never the temp
 * password itself.
 *
 * Companion API endpoints live in sherwood-api/src/controllers/researcher.controller.ts
 * (resetResearcherPasswordV2 + selfChangePasswordV2).
 */
import axios from 'axios';
import { computeLoginSecret, derivePasswordKey, wrapKek } from '../utils/crypto';

const KEK_CATEGORIES = ['personal_data', 'images', 'consents'] as const;
type Category = (typeof KEK_CATEGORIES)[number];

interface ReWrappedKek {
    category: string;
    nonce: string;
    ciphertext: string;
}

interface ReWrappedKekWithInvestigation extends ReWrappedKek {
    investigationUuid: string;
}

function authHeaders() {
    const jwt = localStorage.getItem('jwt');
    return { headers: { Authorization: jwt ?? '' } };
}

/**
 * Read the raw KEK (32 bytes) for (investigationUuid, category) out of localStorage.
 * authService.handleLoginKeks() stores it under `kek_${investigationId}_${category}`
 * after every loginV2.
 */
function loadRawKek(investigationUuid: string, category: Category): Uint8Array {
    const key = `kek_${investigationUuid}_${category}`;
    const b64 = localStorage.getItem(key);
    if (!b64) {
        throw new Error(`Missing raw KEK in localStorage for category=${category}. Re-login required.`);
    }
    return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

/**
 * Generate a cryptographically random one-time temp password for an admin reset.
 * 24 bytes of base64url → ~32 chars, safe to display and copy. We hand this to the
 * admin to deliver out-of-band; the server never sees it in plaintext.
 */
function generateTempPassword(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(24));
    let s = btoa(String.fromCharCode(...bytes));
    return s.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Admin-side reset. The actor must be logged in with the investigation's KEKs
 * unwrapped in localStorage. Returns the plaintext temp password so the caller
 * can show it once to the admin (display-only; never persist).
 */
export async function adminResetPasswordV2(
    investigationUuid: string,
    uuidTargetResearcher: string,
): Promise<{ tempPassword: string }> {
    // 1. Pull the admin's currently-unwrapped raw KEKs from localStorage.
    const rawKeks: Record<Category, Uint8Array> = {} as any;
    for (const category of KEK_CATEGORIES) {
        rawKeks[category] = loadRawKek(investigationUuid, category);
    }

    // 2. Generate the temp password + a fresh provisional wrapping salt.
    const tempPassword = generateTempPassword();
    const provisionalSaltBytes = crypto.getRandomValues(new Uint8Array(16));
    const provisionalKey = await derivePasswordKey(tempPassword, provisionalSaltBytes);

    // 3. Re-wrap each KEK with the provisional key.
    const reWrappedKeks: ReWrappedKek[] = [];
    for (const category of KEK_CATEGORIES) {
        const wrapped = await wrapKek(rawKeks[category], provisionalKey);
        reWrappedKeks.push({ category, ...wrapped });
    }

    // 4. POST. The body's newLoginSecret is the Argon2id hash of the temp password
    //    using the same client-side parameters loginV2 uses — keeps the server's
    //    re-hash idempotent against a future temp-password login.
    const newLoginSecret = await computeLoginSecret(tempPassword);
    const url = `${import.meta.env.VITE_APP_API_URL}/researcher/investigation/${investigationUuid}/reset-password/v2/${uuidTargetResearcher}`;
    await axios.post(
        url,
        {
            newLoginSecret,
            newKekWrappingSalt: btoa(String.fromCharCode(...provisionalSaltBytes)),
            reWrappedKeks,
        },
        authHeaders(),
    );

    // 5. Return temp password for one-time display. Caller is responsible for
    //    NOT logging it, NOT persisting it, and clearing the clipboard on close.
    return { tempPassword };
}

/**
 * Target-side self-change. Used by the forced-change page after admin reset,
 * and also for voluntary rotations.
 *
 * Reads raw KEKs from localStorage (loaded at temp-password login), generates
 * a new keyDerivationSalt, derives the new PDK, re-wraps every cached KEK,
 * and POSTs. On success the server issues a fresh JWT in the `auth` header;
 * we capture it so the caller can swap it in localStorage.
 */
export async function selfChangePasswordV2(
    oldPassword: string,
    newPassword: string,
): Promise<{ jwt: string; newKeyDerivationSalt: string }> {
    // 1. Gather raw KEKs across all (investigationUuid, category) pairs in localStorage.
    //    Use the same prefix authService.handleLoginKeks writes.
    const rawKekEntries: { investigationUuid: string; category: Category; raw: Uint8Array }[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)!;
        const m = key.match(/^kek_(.+)_(personal_data|images|consents)$/);
        if (!m) continue;
        const [, investigationUuid, categoryRaw] = m;
        const category = categoryRaw as Category;
        const b64 = localStorage.getItem(key)!;
        rawKekEntries.push({
            investigationUuid,
            category,
            raw: Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)),
        });
    }
    if (rawKekEntries.length === 0) {
        throw new Error('No KEKs found in local storage. Re-login required.');
    }

    // 2. Derive new PDK from the chosen new password + fresh salt.
    const newKdfSaltBytes = crypto.getRandomValues(new Uint8Array(16));
    const newPdk = await derivePasswordKey(newPassword, newKdfSaltBytes);
    const newKeyDerivationSalt = btoa(String.fromCharCode(...newKdfSaltBytes));

    // 3. Re-wrap each KEK independently — KEKs are per-(researcher, investigation,
    //    category). Different investigations have different raw KEKs for the same
    //    category, so we must NOT de-dupe by category. The server matches each entry
    //    by (researcherId, investigationId, category).
    const reWrappedKeks: ReWrappedKekWithInvestigation[] = [];
    for (const entry of rawKekEntries) {
        const wrapped = await wrapKek(entry.raw, newPdk);
        reWrappedKeks.push({
            investigationUuid: entry.investigationUuid,
            category: entry.category,
            ...wrapped,
        });
    }

    // 4. POST. Both old and new loginSecret use the Argon2id hash of the plaintext
    //    password under the fixed app salt.
    const oldLoginSecret = await computeLoginSecret(oldPassword);
    const newLoginSecret = await computeLoginSecret(newPassword);
    const url = `${import.meta.env.VITE_APP_API_URL}/researcher/self-change-password/v2`;
    const response = await axios.post(
        url,
        {
            oldLoginSecret,
            newLoginSecret,
            newKeyDerivationSalt,
            reWrappedKeks,
        },
        authHeaders(),
    );

    // selfChangePasswordV2 route handler sets the `auth` header on 200 (see
    // sherwood-api/src/routes/researcherRoutes.ts: selfChangePasswordV2Route).
    // The body fallback path matches IReturnLogin's actual shape: { status, result: { jwt } }.
    const jwt = (response.headers as any)['auth'] || response.data?.result?.jwt || response.data?.jwt;
    if (!jwt) {
        throw new Error('Self-change succeeded but no JWT returned');
    }
    return { jwt, newKeyDerivationSalt };
}

/**
 * Verifies whether the currently-logged-in researcher must complete a forced
 * password change. Reads the flag persisted at login (authService writes
 * `mustChangePassword` to localStorage from the loginV2 response body).
 */
export function isMustChangePassword(): boolean {
    return localStorage.getItem('mustChangePassword') === 'true';
}

export function clearMustChangePassword(): void {
    localStorage.removeItem('mustChangePassword');
}
